import { useEffect } from "react";
import { createRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AppLayout } from "@/components/AppLayout";
import { getToken } from "@/auth";
import { evaluateAuthGuard } from "@/lib/auth-guard";
import { useAuth } from "@/contexts/useAuth";
import { useProfile } from "@/hooks/useAdmin";
import { getStoredAnalyticsConsent } from "@/lib/analytics-consent";
import { identifyUser } from "@/lib/posthog";
import { getCookie } from "@takelead/shared";
import { applyLocale } from "@/i18n";
import { toDisplayString, toErrorDisplayMessage } from "@/lib/utils";

/** Wie RouterDefaultError, aber Link zur Übersicht (Nutzer ist eingeloggt). */
function AuthenticatedRouteError({ error }: { error: unknown }) {
  let message = "Unbekannter Fehler.";
  try {
    const s = toErrorDisplayMessage(error);
    if (s.trim()) message = s;
  } catch {
    /* Fehler-UI darf nie selbst werfen */
  }
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 bg-bg p-6">
      <p className="max-w-md text-center text-sm text-danger">{message}</p>
      <Link
        to="/"
        className="rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm font-medium text-text hover:bg-surface"
      >
        Zur Übersicht
      </Link>
    </div>
  );
}

/**
 * Identifiziert den Nutzer in PostHog — ausschließlich über die opake Supabase-User-ID.
 *
 * Vorher stand hier `identifyUser(emailStr, { email: emailStr })`: die E-Mail-Adresse war
 * distinct_id UND wurde zusätzlich als Personen-Property übertragen. Damit lag beim
 * Analytics-Anbieter ein Klartext-Identifier statt eines Pseudonyms, und die serverseitige
 * Erfassung (apps/api/src/routes/analytics.ts) schrieb auf dieselbe Person unter einer
 * anderen ID — zwei Profile pro Nutzer.
 */
function PostHogIdentifyWhenConsented() {
  const { state } = useAuth();
  const { status } = state;
  const userId = status === "authenticated" ? state.userId : null;
  useEffect(() => {
    if (getStoredAnalyticsConsent() !== "accepted") return;
    if (status !== "authenticated") return;
    /** Kein JWT als distinct_id — groß, sensibel und kann SDK-Edgecases triggern. */
    const id = typeof userId === "string" ? userId.trim() : "";
    if (!id) return;
    /** Keine E-Mail als Property: der Identifier bleibt pseudonym. */
    identifyUser(id);
  }, [status, userId]);
  return null;
}

/** Nur für eingeloggte Nutzer: Locale aus Profil, wenn noch kein Cookie gesetzt. */
function SyncLocaleFromProfile() {
  const { state } = useAuth();
  const { data: profile } = useProfile();
  useEffect(() => {
    if (state.status !== "authenticated" || !profile?.preferred_locale) return;
    if (getCookie("takelead_locale")) return;
    void applyLocale(toDisplayString(profile.preferred_locale));
  }, [state.status, profile?.preferred_locale]);
  return null;
}

function AuthenticatedLayout() {
  return (
    <AppLayout>
      <SyncLocaleFromProfile />
      <PostHogIdentifyWhenConsented />
      <Outlet />
    </AppLayout>
  );
}

// Pathless layout route: kein `path`, nur `id` — läuft vor jeder auth-geschützten Route
export const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  beforeLoad: async () => {
    /** The decision lives in lib/auth-guard.ts so it can be tested; the throw stays here. */
    const decision = evaluateAuthGuard(
      await getToken(),
      typeof window !== "undefined"
        ? { search: window.location.search, pathname: window.location.pathname }
        : null,
    );
    if (decision.kind === "redirect") {
      throw redirect({ to: decision.to, search: decision.search });
    }
  },
  component: AuthenticatedLayout,
  errorComponent: AuthenticatedRouteError,
});
